import { Record } from "immutable";
import { DateStringNumber, Nullable } from "common/records";
import { checkDate } from "common/helpers/Date";
import type { OpeningRecord } from "common/records/Opening";

export type OpeningType = "Window" | "Door";

export const openingEstimationFromJSON = (
  json: Partial<IOpeningEstimationData> & { openable?: unknown },
): OpeningEstimationRecord => {
  // Drop `openable` up front — it's client-only (set at attach time) and must never hydrate from API
  // data. The typed rest omits it without an `as any` cast.
  const { openable: _openable, ...data } = json;
  const record: Partial<IOpeningEstimationRecord> = { ...data };

  if (json.created_at) {
    record.created_at = checkDate(json.created_at);
  }

  if (json.updated_at) {
    record.updated_at = checkDate(json.updated_at);
  }

  return new OpeningEstimationRecord(record);
};

export interface IOpeningEstimationData {
  id: number;
  openable_type: OpeningType;
  openable_id: number;
  estimate_line_item_id: number;
  created_at: DateStringNumber;
  updated_at: DateStringNumber;
  _destroy?: boolean;
}

export interface IOpeningEstimationRecord {
  id: number;
  openable_type: Nullable<OpeningType>;
  openable_id: number;
  estimate_line_item_id: number;
  created_at: Nullable<Date>;
  updated_at: Nullable<Date>;
  _destroy: Nullable<boolean>;
  /**
   * Client-side only: the Window/Door record this estimation points at, captured when the wizard
   * attaches an opening. Never populated from API JSON and never serialized (rsf-api permits only
   * id/estimate_line_item_id/openable_id/openable_type/_destroy). It is a fallback, not the source of
   * truth: resolve the opening at the point of use via `firstLineItemOpening` (openingById by
   * id+type, falling back to this `openable`), so pricing/measurement never read a stale embedded
   * copy after the underlying window/door changes.
   */
  openable: Nullable<OpeningRecord>;
}

const defaultOpeningEstimationProps: IOpeningEstimationRecord = {
  id: 0,
  openable_type: null,
  openable_id: 0,
  estimate_line_item_id: 0,
  created_at: null,
  updated_at: null,
  _destroy: null,
  openable: null,
};

export class OpeningEstimationRecord
  extends Record(defaultOpeningEstimationProps)
  implements IOpeningEstimationRecord {}
